import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import faqService from '../services/faqService';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';

/**
 * FAQ Page Component
 *
 * Displays frequently asked questions organized by category with accordion-style layout.
 * Fetches FAQ data from backend API with automatic fallback to hardcoded data.
 *
 * Features:
 * - Dynamic FAQ loading from CMS backend
 * - Automatic fallback to hardcoded data if API fails
 * - Real-time updates via WebSocket
 * - Feature flag control (REACT_APP_USE_CMS_CONTENT)
 * - Accordion-style expand/collapse
 */

// Hardcoded FAQ data - preserved for fallback when API is unavailable or feature flag disabled
const HARDCODED_FAQ_DATA = {
  general: {
    title: "General Questions",
    icon: "fas fa-question-circle",
    questions: [
      {
        id: "gen1",
        question: "What is Touchline Memories?",
        answer: "Touchline Memories is your premier destination for authentic sports memorabilia, featuring iconic moments from football, cricket, rugby, basketball, tennis, and athletics. We specialize in high-quality prints, framed artwork, and collectibles celebrating legendary athletes and historic sporting events."
      },
      {
        id: "gen2",
        question: "Are your products authentic?",
        answer: "Yes, all our products are 100% authentic. We work directly with licensed suppliers and maintain strict quality control standards. Each piece of memorabilia comes with documentation verifying its authenticity where applicable."
      },
      {
        id: "gen3",
        question: "How do I contact customer service?",
        answer: "You can reach our customer service team through our Contact page, by email, or by phone during business hours (Monday-Friday, 9AM-5PM GMT). We typically respond to all inquiries within 24 hours."
      },
      {
        id: "gen4",
        question: "Do you have a physical store?",
        answer: "Currently, we operate as an online-only store. This allows us to offer competitive pricing and serve customers worldwide. However, we're exploring options for pop-up events and exhibitions in the future."
      }
    ]
  },
  ordering: {
    title: "Ordering & Payment",
    icon: "fas fa-shopping-cart",
    questions: [
      {
        id: "ord1",
        question: "What payment methods do you accept?",
        answer: "We accept all major credit and debit cards (Visa, Mastercard, American Express), PayPal, and bank transfers. All payments are processed securely through our encrypted payment gateway."
      },
      {
        id: "ord2",
        question: "Is it safe to use my credit card on your site?",
        answer: "Absolutely. We use industry-standard SSL encryption to protect your personal and payment information. Our site is PCI DSS compliant, and we never store your complete credit card details on our servers."
      },
      {
        id: "ord3",
        question: "Can I modify or cancel my order?",
        answer: "You can modify or cancel your order within 2 hours of placement by contacting our customer service team. After this window, orders enter our fulfillment process and cannot be changed. However, you can still return items within our standard return period."
      },
      {
        id: "ord4",
        question: "Do you offer gift cards or vouchers?",
        answer: "Yes! We offer digital gift cards in various denominations. Gift cards are delivered via email and can be redeemed at checkout. They never expire and make perfect gifts for sports enthusiasts."
      },
      {
        id: "ord5",
        question: "How do I track my order?",
        answer: "Once your order ships, you'll receive a confirmation email with a tracking number. You can also track your order status by logging into your account and visiting the 'My Orders' section."
      }
    ]
  },
  shipping: {
    title: "Shipping & Delivery",
    icon: "fas fa-truck",
    questions: [
      {
        id: "ship1",
        question: "What are your shipping rates?",
        answer: "Shipping rates vary based on destination and order size. Standard UK shipping is £4.95 for orders under £50 and FREE for orders over £50. International shipping rates are calculated at checkout based on your location."
      },
      {
        id: "ship2",
        question: "How long does delivery take?",
        answer: "UK Standard Delivery: 3-5 business days | UK Express Delivery: 1-2 business days | European Union: 5-10 business days | International: 7-21 business days (depending on location and customs clearance)."
      },
      {
        id: "ship3",
        question: "Do you ship internationally?",
        answer: "Yes, we ship to over 100 countries worldwide. International shipping times and costs vary by destination. Please note that international orders may be subject to customs duties and import taxes, which are the responsibility of the recipient."
      },
      {
        id: "ship4",
        question: "Can I expedite my shipping?",
        answer: "Yes, we offer express shipping options for urgent deliveries. Express shipping is available at checkout with guaranteed delivery times. Orders placed before 2PM GMT Monday-Friday will be dispatched the same day for express delivery."
      },
      {
        id: "ship5",
        question: "What if my item arrives damaged?",
        answer: "All items are carefully packaged to prevent damage during transit. If your item arrives damaged, please contact us within 48 hours with photos of the damage and packaging. We'll arrange a replacement or full refund immediately at no additional cost to you."
      }
    ]
  },
  returns: {
    title: "Returns & Refunds",
    icon: "fas fa-undo",
    questions: [
      {
        id: "ret1",
        question: "What is your return policy?",
        answer: "We offer a 30-day return policy for most items. Products must be returned in their original condition with all packaging and documentation. Custom or personalized items cannot be returned unless defective. Return shipping costs are the responsibility of the customer unless the item is faulty."
      },
      {
        id: "ret2",
        question: "How do I initiate a return?",
        answer: "To initiate a return, log into your account and visit 'My Orders'. Select the order containing the item you wish to return and click 'Request Return'. You'll receive return instructions and a return authorization number within 24 hours."
      },
      {
        id: "ret3",
        question: "When will I receive my refund?",
        answer: "Refunds are processed within 5-7 business days of receiving your returned item. The refund will be credited to your original payment method. Please allow an additional 3-5 business days for the refund to appear in your account, depending on your financial institution."
      },
      {
        id: "ret4",
        question: "Can I exchange an item instead of returning it?",
        answer: "Yes! If you'd like to exchange an item for a different size, color, or product, please indicate this when submitting your return request. We'll process your exchange as a priority to minimize any delay in receiving your new item."
      },
      {
        id: "ret5",
        question: "What if I received the wrong item?",
        answer: "If you received an incorrect item, please contact us immediately. We'll arrange for the correct item to be sent to you at no additional charge and provide a prepaid return label for the incorrect item. We sincerely apologize for any inconvenience."
      }
    ]
  },
  products: {
    title: "Products & Care",
    icon: "fas fa-image",
    questions: [
      {
        id: "prod1",
        question: "What materials are your prints made from?",
        answer: "Our prints are produced using museum-quality archival inks on premium heavyweight paper or canvas. Framed pieces feature high-grade frames with UV-protective glass or acrylic to preserve colors and prevent fading for decades."
      },
      {
        id: "prod2",
        question: "How should I care for my memorabilia?",
        answer: "To preserve your memorabilia: Keep out of direct sunlight to prevent fading | Avoid humid environments | Dust frames gently with a soft, dry cloth | For framed items, never use glass cleaner directly on the glass - spray on cloth first | Store unframed prints flat in a cool, dry place."
      },
      {
        id: "prod3",
        question: "Do you offer custom framing?",
        answer: "Currently, we offer a selection of professionally framed options for many of our products. Custom framing services are in development. If you need specific framing requirements, please contact us and we'll do our best to accommodate your request."
      },
      {
        id: "prod4",
        question: "Can I request a specific image or athlete?",
        answer: "We're always expanding our collection! If there's a specific athlete, team, or moment you'd like to see in our catalog, please contact us with your request. While we can't guarantee availability due to licensing restrictions, we'll do our best to source items that match your interests."
      },
      {
        id: "prod5",
        question: "Are your products limited edition?",
        answer: "Some of our products are limited edition runs, which will be clearly marked on the product page. These items include a certificate of authenticity with edition numbering. Once sold out, limited edition items will not be reproduced."
      },
      {
        id: "prod6",
        question: "Do you offer bulk or wholesale pricing?",
        answer: "Yes, we offer special pricing for bulk orders and corporate clients. If you're interested in purchasing multiple items or decorating a sports bar, office, or facility, please contact our sales team for a custom quote."
      }
    ]
  }
};

/**
 * Transform API response to component format
 *
 * API format: Array of sections with nested questions
 * Component format: Object keyed by slug
 *
 * @param {Array} apiData - Array of FAQ sections from API
 * @returns {Object} - FAQ data in component format
 */
function transformAPIDataToComponentFormat(apiData) {
  if (!apiData || !Array.isArray(apiData)) {
    return HARDCODED_FAQ_DATA;
  }

  const transformed = {};

  apiData.forEach(section => {
    // Generate simple IDs for questions (for accordion state management)
    const questions = section.questions.map((q, index) => ({
      id: q.id || `${section.slug}-q${index + 1}`,
      question: q.question,
      answer: q.answer
    }));

    transformed[section.slug] = {
      title: section.title,
      icon: section.icon,
      questions: questions
    };
  });

  return transformed;
}

function FAQ() {
  // FAQ data state: Use hardcoded as initial value
  const [faqData, setFaqData] = useState(HARDCODED_FAQ_DATA);
  const [loading, setLoading] = useState(true);

  // Background image state (null = no banner shown)
  const [backgroundUrl, setBackgroundUrl] = useState(null);

  // Accordion state - Track which FAQ items are open
  const [openItems, setOpenItems] = useState(new Set());

  // Toggle individual FAQ item
  const toggleItem = (itemId) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(itemId)) {
      newOpenItems.delete(itemId);
    } else {
      newOpenItems.add(itemId);
    }
    setOpenItems(newOpenItems);
  };

  // Fetch FAQ from CMS - extracted to reusable function for WebSocket updates
  const fetchFAQ = useCallback(async () => {
    try {
      setLoading(true);

      const result = await faqService.getAllFAQSections();

      if (result.success && result.data) {
        // Transform API data to component format
        const transformed = transformAPIDataToComponentFormat(result.data);
        setFaqData(transformed);

        if (process.env.NODE_ENV === 'development') {
          console.log('[FAQ] Loaded FAQ from CMS API');
        }
      } else {
        // Use hardcoded fallback
        setFaqData(HARDCODED_FAQ_DATA);

        if (process.env.NODE_ENV === 'development') {
          console.warn('[FAQ] Using hardcoded FAQ data:', result.reason || result.error);
        }
      }
    } catch (error) {
      console.error('[FAQ] Unexpected error:', error);
      setFaqData(HARDCODED_FAQ_DATA);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch page background from CMS
  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }

    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[FAQ] Error fetching background:', err);
    }
  }, []);

  // Initial fetch on mount
  useEffect(() => {
    fetchFAQ();
  }, [fetchFAQ]);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  // Listen for real-time FAQ updates via WebSocket
  useContentUpdates({
    onFAQUpdated: (sectionSlug, sectionTitle) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[FAQ] Real-time update: ${sectionTitle} (${sectionSlug || 'Full Sync'})`);
      }

      // Refresh FAQ data
      fetchFAQ();

      // Show notification to user
      if (sectionSlug === "" && sectionTitle === "Full Sync") {
        toast.info('FAQ content has been updated', {
          autoClose: 3000,
          position: 'bottom-right'
        });
      } else {
        toast.info(`FAQ section "${sectionTitle}" has been updated`, {
          autoClose: 3000,
          position: 'bottom-right'
        });
      }
    },

    onFAQDeleted: (sectionSlug) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[FAQ] Section deleted: ${sectionSlug}`);
      }

      // Refresh FAQ data to remove deleted section
      fetchFAQ();

      toast.warning('A FAQ section has been removed', {
        autoClose: 3000,
        position: 'bottom-right'
      });
    },

    onPageBackgroundUpdated: (page) => {
      if (page === 'faq') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={-100}
          className="paralax-section-slide-data1"
        />
      )}

      {/* FAQ Page Content */}
      <div className="faq-page py-5">
        <div className="container">

          {/* Page Header */}
          <div className="row mb-5">
            <div className="col-12 text-center">
              <h1 className="heading mb-3">Frequently Asked Questions</h1>
              <p className="para_text">
                Find answers to common questions about our products, ordering, shipping, and more.
                <br />
                Can't find what you're looking for? <Link to="/contact">Contact us</Link> and we'll be happy to help!
              </p>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading FAQ...</p>
            </div>
          )}

          {/* FAQ Categories */}
          {!loading && Object.entries(faqData).map(([categoryKey, category]) => (
            <div key={categoryKey} className="faq-category mb-5">

              {/* Category Header */}
              <div className="category-header mb-4">
                <h2 className="h3 d-flex align-items-center">
                  <i className={`${category.icon} mr-3`} style={{ color: '#03a9f5' }}></i>
                  {category.title}
                </h2>
                <hr style={{ borderTop: '2px solid #03a9f5', width: '100px', marginLeft: 0 }} />
              </div>

              {/* FAQ Items */}
              <div className="faq-items">
                {category.questions.map((faq) => {
                  const isOpen = openItems.has(faq.id);

                  return (
                    <div key={faq.id} className="faq-item mb-3">
                      <div
                        className={`faq-question${isOpen ? ' faq-question-expanded' : ''}`}
                        onClick={() => toggleItem(faq.id)}
                        style={{
                          padding: '15px 20px',
                          backgroundColor: isOpen ? 'rgba(3, 169, 245, 0.1)' : '#f8f9fa',
                          borderLeft: isOpen ? '4px solid #03a9f5' : '4px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          borderRadius: '4px'
                        }}
                        onMouseEnter={(e) => {
                          if (!isOpen) {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isOpen) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                          }
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <h3 className="h6 mb-0" style={{ fontWeight: isOpen ? '600' : '500' }}>
                            {faq.question}
                          </h3>
                          <i
                            className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`}
                            style={{
                              color: '#03a9f5',
                              transition: 'transform 0.3s ease',
                              fontSize: '14px'
                            }}
                          ></i>
                        </div>
                      </div>

                      {/* FAQ Answer - Collapsible */}
                      <div
                        className="faq-answer"
                        style={{
                          maxHeight: isOpen ? '500px' : '0',
                          overflow: 'hidden',
                          transition: 'max-height 0.3s ease',
                          backgroundColor: '#fff'
                        }}
                      >
                        <div
                          style={{
                            padding: isOpen ? '20px 20px 20px 24px' : '0 20px',
                            opacity: isOpen ? 1 : 0,
                            transition: 'opacity 0.3s ease, padding 0.3s ease',
                            borderLeft: '4px solid #e9ecef',
                            marginTop: isOpen ? '2px' : '0'
                          }}
                        >
                          <div
                            className="mb-0"
                            style={{ color: '#555', lineHeight: '1.8' }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Still Have Questions? */}
          <div className="row mt-5">
            <div className="col-12">
              <div
                className="text-center p-5"
                style={{
                  background: 'linear-gradient(135deg, rgba(3, 169, 245, 0.1) 0%, rgba(3, 169, 245, 0.05) 100%)',
                  borderRadius: '8px',
                  border: '2px dashed rgba(3, 169, 245, 0.3)'
                }}
              >
                <i className="fas fa-question-circle mb-3" style={{ fontSize: '48px', color: '#03a9f5' }}></i>
                <h3 className="h4 mb-3">Still Have Questions?</h3>
                <p className="para_text mb-4">
                  Can't find the answer you're looking for? Our customer service team is here to help.
                </p>
                <Link
                  to="/contact"
                  className="btn pink-gradient-btn-into-black"
                  style={{ minWidth: '200px' }}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}

export default FAQ;
